import { ref } from "vue";
import {
  getServices,
  getService,
  updateService as apiActualUpdateService,
  createService as apiCreateService,
  deleteService as apiDeleteService,
  reorderServicesApi,
} from "../api";
import type {
  Service,
  ServiceUpdate,
  ServiceCreate,
  PaginatedServiceList,
} from "../types";
import type { AdminV1OrganizationsPaymentsServicesListParams } from "@/api/model";

function extractErrorMessage(err: any): string {
  const responseData = err?.response?.data;

  if (responseData && typeof responseData === "object") {
    const errors: string[] = [];

    function collectErrors(obj: any): void {
      for (const key in obj) {
        const value = obj[key];
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (typeof item === "string") {
              errors.push(item);
            } else if (typeof item === "object") {
              collectErrors(item);
            }
          });
        } else if (typeof value === "object" && value !== null) {
          collectErrors(value);
        } else if (typeof value === "string") {
          errors.push(value);
        }
      }
    }

    collectErrors(responseData);

    if (errors.length > 0) {
      return errors.join("; ");
    }
  }

  return String(err.message || err);
}

export function useServices() {
  const services = ref<Service[]>([]);
  const service = ref<Service | null>(null);
  const isLoadingServices = ref(true);
  const isSubmitting = ref(false);
  const error = ref<string | null>(null);
  const reorderError = ref(false);
  const totalServices = ref(0);
  const nextPage = ref<string | null>(null);
  const previousPage = ref<string | null>(null);

  async function fetchServices(
    params?: AdminV1OrganizationsPaymentsServicesListParams
  ): Promise<PaginatedServiceList | Error> {
    isLoadingServices.value = true;
    error.value = null;
    services.value = [];

    try {
      const response = await getServices(params);

      services.value = response.results ?? [];
      totalServices.value = response.count ?? 0;
      nextPage.value = response.next ?? null;
      previousPage.value = response.previous ?? null;
      return response;
    } catch (err) {
      error.value = `Failed to load services: ${String(err)}`;
      return err as Error;
    } finally {
      isLoadingServices.value = false;
    }
  }

  async function fetchServiceById(serviceId: string) {
    isLoadingServices.value = true;
    error.value = null;
    service.value = null;

    try {
      const response = await getService(serviceId);

      service.value = response;
      return response;
    } catch (err) {
      error.value = `Failed to load services: ${String(err)}`;
      return err as Error;
    } finally {
      isLoadingServices.value = false;
    }
  }

  async function updateService(
    serviceId: string,
    serviceUpdateData: ServiceUpdate
  ) {
    isSubmitting.value = true;
    error.value = null;

    try {
      if (!service.value) {
        throw new Error("Cannot update without a loaded service.");
      }
      const response = await apiActualUpdateService(
        serviceId,
        serviceUpdateData,
        service.value
      );

      service.value = response;
    } catch (err: any) {
      console.error("Error in updateService:", err);
      const errorMessage = extractErrorMessage(err);
      error.value = `Failed to update service: ${errorMessage}`;
    } finally {
      isSubmitting.value = false;
    }
  }

  async function createService(
    serviceCreateData: ServiceCreate
  ): Promise<Service | undefined> {
    isSubmitting.value = true;
    error.value = null;

    try {
      const newService = await apiCreateService(serviceCreateData);
      services.value.push(newService);
      service.value = newService;
      return newService;
    } catch (err: any) {
      console.error("Error in createService:", err);
      const errorMessage = extractErrorMessage(err);
      error.value = `Failed to create service: ${errorMessage}`;
    } finally {
      isSubmitting.value = false;
    }
  }

  async function deleteService(serviceId: string): Promise<void> {
    isSubmitting.value = true;
    error.value = null;

    try {
      await apiDeleteService(serviceId);
    } catch (err: any) {
      console.error("Error in deleteService:", err);
      error.value = `Failed to delete service: ${String(err.message || err)}`;
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  }

  async function reorderServices(serviceIds: string[]) {
    reorderError.value = false;
    error.value = null;

    try {
      await reorderServicesApi(serviceIds);
    } catch (err: any) {
      reorderError.value = true;
      error.value = err.message ?? "Reorder failed";
      throw err;
    }
  }

  return {
    services,
    service,
    isLoadingServices,
    isSubmitting,
    error,
    reorderError,
    totalServices,
    nextPage,
    previousPage,
    fetchServices,
    fetchServiceById,
    updateService,
    createService,
    deleteService,
    reorderServices,
  };
}
